import React, { useState, useEffect } from 'react';
import { Badge, ListGroup, Card, Row, Col, Container, Spinner } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown'; // Added Markdown support
import api from '../../api/api';
import AppHeader from '../../components/Common/AppHeader';
import AppFooter from '../../components/Common/AppFooter';

const ReleaseNotesPage = () => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNoteIndex, setSelectedNoteIndex] = useState(0);

    useEffect(() => {
        const fetchNotes = async () => {
            try {
                const res = await api.get('/public/release-notes');
                setNotes(res.data);
            } catch (err) {
                console.error("Failed to load release notes:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchNotes();
    }, []);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <Spinner animation="border" variant="primary" aria-hidden="true" />
            </div>
        );
    }

    const currentNote = notes[selectedNoteIndex];

    return (
        <div className="bg-light min-vh-100 d-flex flex-column">
            
            <Container fluid className="py-4 flex-grow-1">
                <Row className="h-100">
                    {/* Sidebar Archive */}
                    <Col md={3} lg={2} className="border-end">
                        <h6 className="text-uppercase fw-bold text-muted small mb-3 text-center">Release Archive</h6>
                        <ListGroup variant="flush" className="rounded shadow-sm">
                            {notes.length > 0 ? notes.map((note, index) => (
                                <ListGroup.Item 
                                    key={note.id} 
                                    action 
                                    active={selectedNoteIndex === index}
                                    onClick={() => setSelectedNoteIndex(index)}
                                    className="border-0 small py-3"
                                >
                                    <div className="fw-bold">{note.version}</div>
                                    <div className="text-muted x-small">
                                        {new Date(note.release_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                    </div>
                                </ListGroup.Item>
                            )) : (
                                <div className="text-muted small p-3 text-center">No releases found.</div>
                            )}
                        </ListGroup>
                    </Col>

                    {/* Main Body with Markdown Parsing */}
                    <Col md={9} lg={10} className="ps-lg-5">
                        {currentNote ? (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                                    <div>
                                        <h2 className="fw-extrabold text-dark mb-1">{currentNote.title}</h2>
                                        <div className="text-muted small">
                                            Version {currentNote.version} • Published {new Date(currentNote.release_date).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <Badge bg={currentNote.is_staging_only ? "warning" : "primary"} pill className="px-3 py-2">
                                        {currentNote.category}
                                    </Badge>
                                </div>

                                <Card className="border-0 shadow-sm mb-5">
                                    <Card.Body className="p-4 markdown-body">
                                        {/* REPLACED: static div with ReactMarkdown.
                                            This parses bullet points (- item), bold (**text**), and headers (# Header) 
                                        */}
                                        <ReactMarkdown>
                                            {currentNote.description}
                                        </ReactMarkdown>
                                    </Card.Body>
                                </Card>

                                {currentNote.is_staging_only && (
                                    <div className="alert alert-info small border-0 shadow-sm">
                                        <strong>Staging Release:</strong> This version is currently undergoing final QA before production deployment.
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-5 text-muted">Select a version to view updates.</div>
                        )}
                    </Col>
                </Row>
            </Container>
            <AppFooter />
            
            {/* Custom CSS for Markdown Elements */}
            <style>{`
                .markdown-body ul { padding-left: 1.5rem; margin-bottom: 1rem; }
                .markdown-body li { margin-bottom: 0.5rem; color: #4a5568; font-size: 0.95rem; }
                .markdown-body h1, .markdown-body h2, .markdown-body h3 { color: #2d3748; margin-top: 1.5rem; }
                .markdown-body strong { color: #1a202c; }
                .markdown-body p { color: #4a5568; line-height: 1.6; }
            `}</style>
        </div>
    );
};

export default ReleaseNotesPage;